import type { FetchFunction } from '.';
import { create_state, create_simple_state } from './create_state.svelte';
import { graph_utils } from './graph_utils';

export type AuthSigninSignupResponse = { data: AuthData; error: null } | { error: string; data: null };
export type AuthData = { user_id: string; access_token: string; refresh_token: string, extra_id?: string }
type RefreshTokenResponse = { data: { access_token: string }; error: null } | { data: null; error: string };

type PostSignInFlowFn = ((data: AuthData) => (boolean | Promise<boolean>))

const on_authenticated_fns_map = new Map<string, () => any>();
const on_change_fns_map = new Map<string, (state: boolean) => any>();
const on_before_sign_out_fns_map = new Map<string, () => any>();
const on_token_change_fns_map = new Map<string, (token: string | null) => any>();

const browser = !(typeof window === 'undefined');
const clean_object = graph_utils.clean_object;

const jwt_handlers = {
   get_jwt_expiration_timestamp: (token: string) => {
      if (!token) return 0;
      const decoded_token = atob(token.split('.')[1]); // Decode the payload part of the token
      const parsed_payload = JSON.parse(decoded_token);

      if (typeof parsed_payload.exp === 'number')
         return parsed_payload.exp * 1000; // Return the expiration timestamp in milliseconds
      else return 0; // No expiration date found in the token
   },

   validate_token: (token: string) => {
      if (!token) return false;
      if (Date.now() < jwt_handlers.get_jwt_expiration_timestamp(token)) return true;
      return false;
   }
};

function auth_sdk_fn() {
   let token_refresh_timeout: NodeJS.Timeout | null = null;
   let post_sign_in_flow_fn: PostSignInFlowFn | null = null;
   let auth_state_value = create_state({ initial_value: false });
   let is_authenticated_offline = create_simple_state(false);
   let AUTH_ENDPOINT = '';
   let REQUESTED_ROLE = ''
   let await_authenticated_resolve: Promise<void> | null = null

   let fetch_function: <T>({
      access_token,
      url,
      body,
      clean_object
   }: FetchFunction) => Promise<T | null>;

   const tokens_store = create_persistent_storage<AuthData>({
      initial_value: { user_id: '', access_token: '', refresh_token: '', extra_id: undefined },
      store_name: 'graphdb_tokens'
   });

   function create_refresh_timeout() {
      const tokens = tokens_store.value;
      if (!tokens || !tokens.user_id) return tokens_store.reset();

      const { access_token, refresh_token } = tokens;

      if (!access_token || !refresh_token) return null;
      const now = Date.now();
      const token_expiry = jwt_handlers.get_jwt_expiration_timestamp(access_token);

      if (token_refresh_timeout) clearTimeout(token_refresh_timeout);

      if (now > token_expiry) {
         console.log('token expired refreshing now');
         token_refresh_timeout = setTimeout(() => perform_refresh_token(), 0);
         return;
      }

      token_refresh_timeout = setTimeout(() => perform_refresh_token(), token_expiry - now - 60000);
      change_auth_state(true);
      on_token_change_handler(access_token)
      console.log(
         `token is ok, trying refresh in: ${((token_expiry - now - 60000) / 1000 / 60).toFixed(2)} minutes`
      );
   }

   type InitOptions = { auth_endpoint: string; fetch_function: typeof fetch_function; requested_role?: string };

   const handlers = {
      init: (options?: InitOptions) => {
         if (options?.fetch_function) fetch_function = options.fetch_function;
         if (options?.auth_endpoint) AUTH_ENDPOINT = options.auth_endpoint;
         if (options?.requested_role) REQUESTED_ROLE = options.requested_role;

         if (!browser || !AUTH_ENDPOINT) return;
         validate_refresh_token_for_offline_use()
         create_refresh_timeout();
      },

      sign_in: async (
         username: string,
         password: string,
         extra_headers?: Record<string, string>
      ): Promise<{ success: boolean; error: string | null }> => {
         try {
            if (!username || !password) throw new Error('insufficient function arguments');
            console.log({ AUTH_ENDPOINT });
            const url = `${AUTH_ENDPOINT}/login`;
            const body = { username, password, requested_role: REQUESTED_ROLE };

            const fetch_data = await fetch_function<AuthSigninSignupResponse>({
               body,
               url,
               clean_object,
               extra_headers
            });
            if (!fetch_data) throw new Error('fetch_failed');

            console.log({ fetch_data });
            const { data, error } = fetch_data;
            if (error || !data) throw new Error('login_credintials_wrong');

            // console.log({ sign_in_auth_data: data });

            tokens_store.value = data;

            if (post_sign_in_flow_fn) {
               const post_flow_success = await post_sign_in_flow_fn(data);
               if (!post_flow_success) throw new Error('post_flow_failed');
            }

            // change_auth_state(false);
            change_auth_state(true);
            on_token_change_handler(data.access_token)

            return { success: true, error: null };
         } catch (error: any) {
            console.error({ error });
            change_auth_state(false);
            return { success: false, error };
         }
      },

      sign_up: async (
         username: string,
         password: string,
         name?: string,
         extra_headers?: Record<string, string>
      ): Promise<{ success: boolean; error: string | null }> => {
         try {
            if (!username || !password) throw new Error('insufficient function arguments');

            const url = `${AUTH_ENDPOINT}/register`;
            const body = { username, password, name: name ?? null, requested_role: REQUESTED_ROLE };

            const fetch_data = await fetch_function<AuthSigninSignupResponse>({
               body,
               url,
               extra_headers,
               clean_object
            });
            if (!fetch_data) throw new Error('fetch_failed');

            const { data, error } = fetch_data;

            if (error || !data) throw new Error(error);

            tokens_store.value = data;

            if (post_sign_in_flow_fn) {
               const post_flow_success = await post_sign_in_flow_fn(data);
               if (!post_flow_success) throw new Error('post_flow_failed');
            }

            change_auth_state(false);
            change_auth_state(true);
            on_token_change_handler(data.access_token)

            return { success: true, error: null };
         } catch (error: any) {
            console.error('error at sign_up: ', { error });
            change_auth_state(false);
            return { success: false, error };
         }
      },

      sign_out: async (): Promise<
         { success: true; error: null } | { success: false; error: any }
      > => {
         try {
            const access_token = await handlers.get_access_token();
            if (!access_token) throw new Error('no access token');
            const { refresh_token = '' } = tokens_store.value;

            if (on_before_sign_out_fns_map.size) {
               await Promise.all(Array.from(on_before_sign_out_fns_map.values()).map((callback) => callback?.())).catch(console.error);
            }

            const url = `${AUTH_ENDPOINT}/logout`;
            const fetch_data = await fetch_function<{ success: boolean }>({
               access_token,
               url,
               body: { refresh_token },
               clean_object
            });
            if (!fetch_data) throw new Error('fetch_failed');

            const { success } = fetch_data;
            if (!success) throw new Error('logout_fail');

            return { success: true, error: null };
         } catch (error) {
            console.error(`error in sign_out_handler: ${error}`);
            return { success: false, error };
         } finally {
            tokens_store.reset();
            change_auth_state(false);
         }
      },

      get_user_id: (): string => {
         return tokens_store.value.user_id;
      },

      get_access_token: async (): Promise<string> => {
         const current_token = tokens_store.value.access_token;
         if (jwt_handlers.validate_token(current_token)) return current_token;
         const { access_token } = await perform_refresh_token();

         return access_token ?? '';
      },

      on_auth: (id: string, call_back: () => any) => {
         on_authenticated_fns_map.set(id, call_back);
         if (auth_state_value.value) call_back();
         return () => { on_authenticated_fns_map.delete(id) }
      },

      on_change: (id: string, call_back: (state: boolean) => any) => {
         on_change_fns_map.set(id, call_back);
         call_back(auth_state_value.value);
         return () => { on_change_fns_map.delete(id) }
      },

      on_before_sign_out: (id: string = graph_utils.random_string(), call_back: () => any) => {
         on_before_sign_out_fns_map.set(id, call_back);

         return () => { on_before_sign_out_fns_map.delete(id) }
      },


      on_token_change: (id: string, call_back: (token: string | null) => any) => {
         on_token_change_fns_map.set(id, call_back);

         if (jwt_handlers.validate_token(tokens_store.value.access_token)) call_back(tokens_store.value.access_token)
         else call_back(null)
         return () => { on_token_change_fns_map.delete(id) }
      }
   };

   async function validate_refresh_token_for_offline_use() {
      const refresh_token_expiry = jwt_handlers.get_jwt_expiration_timestamp(tokens_store.value.refresh_token);

      if (Date.now() > refresh_token_expiry) return tokens_store.reset()

      console.log('refresh token is ok... is_authenticated_offline is true');
      is_authenticated_offline.value = true;
   }


   async function perform_refresh_token() {
      let no_tokens = false;
      try {
         const tokens = tokens_store.value;
         if (!tokens || !tokens.user_id) {
            no_tokens = true;
            throw 'no_stored_tokens';
         }
         const { access_token: initial_access_token, refresh_token } = tokens;
         if (!initial_access_token || !refresh_token) {
            no_tokens = true;
            throw 'no_stored_tokens';
         }


         const url = `${AUTH_ENDPOINT}/refresh`;

         const fetch_data = await fetch_function<RefreshTokenResponse>({
            url,
            access_token: initial_access_token,
            body: { refresh_token },
            clean_object
         });

         if (!fetch_data?.data?.access_token || fetch_data.error) throw 'fetch_failed';

         const access_token = fetch_data.data.access_token;

         console.log({ perform_refresh_token: fetch_data.data });

         tokens_store.value.access_token = access_token;
         change_auth_state(true);
         on_token_change_handler(access_token)

         const now = Date.now();
         const token_expiry = jwt_handlers.get_jwt_expiration_timestamp(access_token);

         if (token_refresh_timeout) clearTimeout(token_refresh_timeout);
         token_refresh_timeout = setTimeout(() => perform_refresh_token(), token_expiry - now - 60000);

         return { success: true, error: null, access_token };
      } catch (error) {
         // console.error('error at perform_refresh_token: ', { error })
         on_token_change_handler(null)
         if (token_refresh_timeout) clearTimeout(token_refresh_timeout);

         if (error === 'fetch_failed') {
            console.error('fetch_failed error at perform_refresh_token: ')
            change_auth_state(false);
            tokens_store.reset();
            return { success: false, error, access_token: null };
         }

         // console.log({ no_tokens })
         if (!no_tokens) token_refresh_timeout = setTimeout(() => perform_refresh_token(), 5000);

         return { success: false, error, access_token: null };
      }
   }

   async function change_auth_state(change_value: boolean) {
      const previous_state = JSON.parse(JSON.stringify(auth_state_value.value));
      auth_state_value.value = change_value;
      if (change_value === false) {
         is_authenticated_offline.value = false
         on_token_change_handler(null)
      }

      on_change_handler(change_value);
      if (!previous_state && change_value === true) on_authenticated_handler();
   }


   async function await_authenticated() {
      console.log('awaiting authentication')
      if (auth_state_value.value) return true
      if (await_authenticated_resolve) return await_authenticated_resolve

      await_authenticated_resolve = new Promise<void>((resolve) => {
         const unsub = auth_state_value.on_change((value) => {
            if (value) {
               unsub()
               resolve()
            }
         })
      })

      return await_authenticated_resolve
   }

   return {
      ...handlers,
      tokens_store,
      change_auth_state,
      set post_sign_in_flow(value: PostSignInFlowFn) {
         post_sign_in_flow_fn = value;
      },
      get post_sign_in_flow() {
         return post_sign_in_flow_fn as PostSignInFlowFn;
      },
      auth_state_value,
      get is_authenticated_offline() {
         return is_authenticated_offline.value
      },
      await_authenticated,
      get is_authenticated() {
         return auth_state_value.value;
      }
   };
}

function on_authenticated_handler() {
   // console.log(on_authenticated_fns_map.entries())
   on_authenticated_fns_map.forEach((callback) => callback?.());
}

function on_change_handler(state: boolean) {
   // console.log(on_change_fns_map.entries())
   on_change_fns_map.forEach((callback) => callback?.(state));
}

function on_token_change_handler(token: string | null) {
   // console.log(on_token_change_fns_map.entries())
   on_token_change_fns_map.forEach((callback) => callback?.(token));
}


function create_persistent_storage<T>({
   initial_value,
   store_name
}: {
   initial_value: T;
   store_name: string;
}) {
   const storage_string = browser ? localStorage.getItem(store_name) : '';
   let storage_value = storage_string ? JSON.parse(storage_string) : initial_value;

   return {
      get value() {
         return storage_value;
      },
      set value(value: T) {
         storage_value = value;
         if (browser) localStorage.setItem(store_name, JSON.stringify(storage_value));
      },
      reset: () => {
         storage_value = initial_value;
         if (browser) localStorage.setItem(store_name, JSON.stringify(storage_value));
      }
   };
}

export const auth_sdk = auth_sdk_fn();
